export const compliance = {
  list: {
    chaseFailedBody: "Check your connection and try again. If it still fails, open the document and validate it yourself or ask the validator directly.",
    chaseFailedTitle: "Reminder not sent",
    chaseSentBodyUnnamed: "The validator gets a notification to validate it. Each person gets at most one reminder a day.",
    chaseSentBody: "{{name}} gets a notification to validate it. Each person gets at most one reminder a day.",
    chaseSentTitle: "Reminder sent",
    showWorkspace: "Show the whole workspace",
    noWorkspaceDescription: "Compliance is shown for a workspace. Join or create one to see it here.",
    title: 'Compliance',
    subtitle_one: '{{count}} requirement',
    subtitle_other: '{{count}} requirements',
    searchPlaceholder: 'Search assets, documents',
    searchA11y: 'Search compliance',
    refine: 'Refine',
    refineSheetTitle: 'Refine compliance',
    clearFilters: 'Clear filters',
    loadMore: 'Load more',
    emptyTitle: 'Nothing to prove yet',
    emptyDescription: "Open an asset and add a certificate or other document with an expiry date. It then shows here with when it is due.",
    emptyAction: 'Go to assets',
    noMatchTitle: 'No requirements match',
    noMatchDescription: 'No compliance requirements match the filters you have applied.',
    errorTitle: 'Compliance could not be loaded',
    unscopedTitle: 'Pick a workspace',
    unscopedDescription: "Compliance covers the whole workspace, and you are viewing only your own items. Tap Show the whole workspace.",
  },
  // One per state. `Unknown` is deliberately not a verdict.
  bucket: {
    compliant: 'OK',
    expiring: 'Expiring',
    overdue: 'Overdue',
    invalid: 'Rejected',
    pending: 'Awaiting validation',
    missing_evidence: 'Missing',
    unknown: 'Unknown',
  },
  bucketA11y: {
    compliant_one: '{{count}} compliant',
    compliant_other: '{{count}} compliant',
    expiring_one: '{{count}} expiring',
    expiring_other: '{{count}} expiring',
    overdue_one: '{{count}} overdue',
    overdue_other: '{{count}} overdue',
    invalid_one: '{{count}} rejected',
    invalid_other: '{{count}} rejected',
    pending_one: '{{count}} awaiting validation',
    pending_other: '{{count}} awaiting validation',
    missing_evidence: '{{count}} missing evidence',
    unknown_one: '{{count}} unknown',
    unknown_other: '{{count}} unknown',
  },
  /**
   * The state, in words. The spine's colour is decorative and unreadable to a
   * screen reader, so this line carries the meaning on its own.
   */
  state: {
    overdue: 'overdue',
    overdueBy_one: 'overdue by {{count}} day',
    overdueBy_other: 'overdue by {{count}} days',
    expiring: 'expiring',
    expiresToday: 'expires today',
    expiresIn_one: 'expires in {{count}} day',
    expiresIn_other: 'expires in {{count}} days',
    evidenceMissing: 'evidence missing',
    rejected: 'rejected',
    awaitingValidation: 'awaiting validation',
    nothingRecorded: 'nothing recorded',
    inDate: 'in date',
    validFor_one: 'valid for {{count}} day',
    validFor_other: 'valid for {{count}} days',
  },
  action: {
    upload_evidence: 'Add evidence',
    renew: 'Renew',
    replace_rejected: "Replace",
    chase_validation: "Remind validator",
    record_expiry: 'Add expiry date',
    review_asset: 'Review asset',
    none: '',
    // The other answer to the same row: go and look at the thing, rather than
    // chase the paperwork. Opens the shared planning sheet.
    schedule_inspection: 'Inspect',
  },
  row: {
    responsible: 'Responsible: {{name}}',
    unassigned: "No named approver",
    // Provenance. A date that decides whether a site can open should say where
    // it came from.
    sourceLocalRead: 'Read from the document',
    sourceCloudAi: 'Suggested by AI',
    sourceManual: 'Entered by hand',
    validatedBy: 'Accepted by {{name}}',
    openA11y: 'Open {{label}} on {{asset}}',
  },
  refine: {
    status: 'Status',
    site: 'Site',
    siteAny: 'Any site',
    team: 'Team',
    teamAny: 'Any team',
    assetType: 'Asset type',
    asset: 'Asset',
    requirement: 'Requirement',
    due: 'Due',
    dueAny: 'Any time',
    due30: 'Next 30 days',
    due90: 'Next 90 days',
    sortBy: 'Order by',
    groupBy: 'Group by',
  },
  sort: {
    due_soonest: 'Due soonest',
    severity: 'Most urgent',
    asset_az: 'Asset A-Z',
  },
  group: {
    none: 'No grouping',
    status: 'Status',
    asset: 'Asset',
    site: 'Site',
    requirement: 'Requirement',
    noSite: 'No site',
    noRequirement: 'Nothing declared',
    // The last section on screen is still filling: say so rather than show a
    // number the next page contradicts.
    partialCount: '{{count}}+',
  },
  assetDetail: {
    viewRegister: 'View compliance',
  },
  // ESG and sustainability (tracker 44).
  esg: {
    loading: "Loading ESG figures…",
    loadFailed: "Could not load the ESG figures.",
    retry: "Try again",
    notCoveredTitle: "ESG is not on for this site",
    notCoveredBody: "Your workspace has ESG on chosen sites only. An admin can add this site on Plan and billing.",
    noMetersTitle: "No energy readings yet",
    noMetersBody: "Add a meter (electricity, gas, oil, heat or water) to an asset and record readings. Energy and carbon appear here the next day, or straight away with Refresh.",
    byFuelTitle: "Energy by fuel (last 12 months)",
    byMonthTitle: "Energy by month",
    byMonthA11y: "Bar chart of energy used each month for the last 12 months",
    unconverted: "Some readings use a unit we cannot convert to kWh, so they are left out. Check the meter's unit.",
    estimateNote: "Carbon is an estimate using UK government conversion factors ({{year}}). Scope 1 is fuel burnt on site; scope 2 is electricity bought.",
    updatedAt: "Figures worked out {{when}}",
    notYetComputed: "Not worked out yet. Tap Refresh.",
    refresh: "Refresh",
    guideMe: "Guide me",
    refreshing: "Refreshing…",
    edit: "Edit",
    save: "Save",
    cancel: "Cancel",
    remove: "Remove",
    saveFailed: "Could not save. Check your connection and try again.",
    floorAreaTitle: "Floor area",
    floorAreaLabel: "Floor area (m²)",
    floorAreaHint: "Used for energy and carbon per m². Include the sites inside this one only if they have no floor area of their own.",
    floorAreaInvalid: "Enter a number of square metres.",
    floorAreaValue: "{{m2}} m²",
    kpi: {
      energy: "Energy",
      carbon: "Carbon",
      intensity: "kWh per m²",
      intensityUnit: "last 12 months",
      needsFloorArea: "Add the floor area",
      meters: "Meters reporting",
      metersHint: "read in the last 3 months",
      water: "Water",
      vsLastYear: "{{change}} on the year before",
      last12m: "last 12 months",
      scopes: "Scope 1 {{s1}} · Scope 2 {{s2}}",
      same: "No change",
      lower: "↓ {{pct}}%",
      higher: "↑ {{pct}}%",
      electricity: "Electricity",
      electricityCarbon: "{{tonnes}} t CO₂e, last 12 months",
      refrigerant: "Refrigerant",
      refrigerantGwp: "{{refrigerant}}, GWP {{gwp}}",
      fgasSize: "F-gas size",
      fgasFormula: "{{kg}} kg × GWP {{gwp}}",
      fgasUnknownGwp: "GWP not known for this gas",
      leaked: "Leaked, last 12 months",
      leakedTonnes: "{{tonnes}} t CO₂e",
      carbonCaption: "vs last year, estimate",
      vsLastYearShort: "vs last year",
      perM2: "Energy per m²",
      perM2Caption: "last 12 months",
      allFuels: "all fuels, last 12 months",
    },
    fuel: {
      electricity: "Electricity",
      gas: "Gas",
      heat: "Heat",
      oil: "Oil",
      water: "Water",
    },
    refrigerant: {
      title: "Refrigerant (F-gas)",
      none: "No refrigerant recorded.",
      add: "Add refrigerant",
      summary_one: "{{count}} asset holds {{tonnes}} t CO₂e of refrigerant; {{inBand}} need leak checks.",
      summary_other: "{{count}} assets hold {{tonnes}} t CO₂e of refrigerant; {{inBand}} need leak checks.",
      overdue_one: "{{count}} leak check is overdue.",
      overdue_other: "{{count}} leak checks are overdue.",
      assetLine: "{{refrigerant}}, {{kg}} kg ({{tonnes}} t CO₂e)",
      checkEvery: "Leak check every {{months}} months. Next due {{next}}.",
      noCheckNeeded: "Below the F-gas threshold: no regular leak check needed.",
      leakDetectionRequired: "At this size the rules require a leak detection system.",
      added12m: "{{kg}} kg added in the last 12 months",
      typeLabel: "Refrigerant",
      chargeLabel: "Charge (kg)",
      chargeInvalid: "Enter the charge in kg.",
      leakDetection: "Leak detection fitted",
      hermetic: "Hermetically sealed",
      previewBand: "{{tonnes}} t CO₂e: leak check every {{months}} months.",
      previewNone: "{{tonnes}} t CO₂e: no regular leak check needed.",
      planNote: "We add the leak check to this asset's plan and keep it at the right interval.",
      logCheck: "Log leak check",
      logLeak: "Log a leak",
      logTopUp: "Log top-up",
      logRecovered: "Log recovered",
      kgLabel: "Amount (kg)",
      removeTitle: "Remove refrigerant?",
      removeBody: "The leak check plan for this asset ends. The log of past checks is kept.",
      edit: "Edit refrigerant",
      fromDetails: "Gas from the asset's Details: {{refrigerant}}",
    },
    epc: {
      title: "Energy certificates",
      none: "No certificate recorded.",
      add: "Add certificate",
      ratingLabel: "Rating",
      validUntilLabel: "Valid until (YYYY-MM-DD)",
      validUntil: "valid until {{date}}",
      dateInvalid: "Enter the date as YYYY-MM-DD.",
      refLabel: "Certificate number",
      isLet: "The building is let",
      belowMinimum: "Below E: a let building in England and Wales cannot be let at this rating without an exemption.",
      expired: "Expired",
      expiresSoon: "Expires within 6 months",
      documentNote: "Keep the certificate itself under Documents as Energy performance certificate, so it is reminded before it runs out.",
      removeTitle: "Remove this certificate?",
      let: "Let",
      notLet: "Not let",
      meetsStandard: "Meets the letting minimum",
      open: "Open certificate",
      attachFile: "Attach the file",
      noFile: "No file attached yet",
      addRating: "Add the rating",
      keptInDocuments: "Certificates are kept once, under Documents. The rating, date and reminder come from there.",
    },
    sg: {
      title: "Social and governance",
      hint: "From your inspections and jobs in the last 12 months.",
      compliance: "Compliance",
      risk: "Average risk",
      inspections: "Inspections",
      signedOff: "{{done}} done, {{signed}} signed off",
      jobs: "Jobs",
      rework: "{{done}} done, {{rework}} reworked",
      contractors: "Contractors used",
      tile: {
        contractors: "Contractors used",
        contractorsCaption: "insurance not recorded yet",
        safety: "Safety certificates in date",
        findings: "Findings fixed on time",
        signedOff: "Planned work signed off",
        ofCount: "{{done}} of {{total}}",
        nothingYet: "nothing to count yet",
      },
    },
    promptTitle: "ESG and sustainability",
    promptBody: "See energy, carbon, refrigerant and energy certificates for every site and asset, worked out from the readings you already take. An add-on, priced per asset.",
    promptAction: "Add it on Plan and billing",
    promptAskAdmin: "Ask a workspace admin to add it.",
    promptNotInApp: "Not available to buy in the app.",
    ai: {
      title: "Write it up",
      hint: "A short summary of these figures with recommended actions. Every number comes from above.",
      reader: {
        manager: "For me",
        landlord: "For a landlord",
        investor: "For an investor",
      },
      write: "Write it up",
      again: "Write again",
      writing: "Writing…",
      failed: "Could not write it up. Try again.",
      noCredits: "Your workspace has no AI credits left.",
      plainNote: "A plain write-up, with no AI credit used.",
      actionsTitle: "Recommended actions",
      do: {
        job: "Create job",
        inspection: "Inspect",
        reading: "Add reading",
        record: "Open",
      },
    },
    portfolio: {
      title: "Portfolio ESG",
      hint: "Each top-level site, last 12 months.",
      site: "Site",
      energy: "Energy",
      change: "Change",
      intensity: "kWh/m²",
      carbon: "Carbon",
      epc: "EPC",
      fgas: "F-gas",
      overdue: "overdue",
      belowMinimum: "below E",
      expiring: "expiring",
      empty: "No sites yet.",
      workspaceTitle: "Whole workspace",
    },
    period: "{{from}} to {{to}}",
    addFloorArea: "Add floor area",
    addonBadge: "ESG add-on",
    promptTitleAsset: "ESG for this asset",
    promptTitleSite: "ESG for this site",
    promptMeters_one: "You already record {{count}} meter here ({{kinds}}). With the ESG add-on its readings become energy, carbon and year-on-year figures straight away.",
    promptMeters_other: "You already record {{count}} meters here ({{kinds}}). With the ESG add-on their readings become energy, carbon and year-on-year figures straight away.",
    promptNothingHidden: "Nothing you record is hidden or lost: your meters, readings and documents stay as they are.",
    header: {
      floorArea: "{{value}} m²",
      rating: "{{scheme}} {{rating}}",
    },
    csv: {
      defaultName: "ESG",
    },
    actions: {
      exportCsv: "Export ESG CSV",
      share: "Share ESG report",
      openAsset360: "Open Asset 360",
      openSite360: "Open Site 360",
    },
    chart: {
      electricityTitle: "Electricity by month",
      energyTitle: "Energy by month",
      subElectricity_one: "MWh, from {{count}} meter",
      subElectricity_other: "MWh, from {{count}} meters",
      subEnergy_one: "MWh, all fuels, from {{count}} meter",
      subEnergy_other: "MWh, all fuels, from {{count}} meters",
      a11y: "{{title}}: bars for each of the last 12 months, a dashed line for the same month last year",
      valueWithLastYear: "{{month}}: {{value}} MWh (last year {{last}} MWh)",
      value: "{{month}}: {{value}} MWh",
      tapHint: "Tap a bar to see its value.",
      thisYear: "This year",
      lastYear: "Last year",
      metersReporting: "{{reporting}} of {{total}} meters reporting (read in the last 3 months)",
    },
    carbon: {
      title: "Where the carbon comes from",
      sub: "Last 12 months, estimate using {{year}} factors",
      scope: "Scope {{n}}",
      refrigerantLeak: "Refrigerant leak, {{name}}",
      none: "No carbon worked out yet. Add meter readings or log refrigerant top-ups.",
      fuelsTitle: "Energy by fuel",
    },
    fgas: {
      titleWithEpc: "F-gas and energy certificates",
      none: "No refrigerant recorded at this site.",
      row: "{{name}}: {{refrigerant}}, {{kg}} kg ({{tonnes}} t CO₂e)",
      overdue: "Leak check overdue",
      due: "Leak check due {{date}}",
      noCheck: "No leak check needed",
      openJobFor: "Open job for {{name}}",
      bookFor: "Book leak check for {{name}}",
      jobTitle: "F-gas leak check: {{name}}",
      jobDescription: "Leak check of the {{refrigerant}} system ({{kg}} kg, {{tonnes}} t CO₂e) by a certified engineer. Record the result and any refrigerant added.",
    },
    leak: {
      title: "Leak checks",
      sub: "How often the F-gas rules ask for a leak check, by size in t CO₂e.",
      every: "Every {{months}} months",
      noneNeeded: "Not needed",
      band: {
        none: "Under 5 t",
        "5-50": "5 to 50 t",
        "50-500": "50 to 500 t",
        "500+": "500 t and over",
      },
      lastCheck: "Last check {{date}}: {{result}}",
      resultLeak: "leak found",
      resultPassed: "passed",
      noCheckYet: "No leak check logged yet.",
      overdue: "Overdue since {{date}}",
      nextDue: "Next due {{date}}",
      openJob: "Open job",
      logTitle: "Leak log",
      logEmpty: "Nothing logged yet.",
      event: {
        leak_check: "Leak check",
        leak_found: "Leak found",
        top_up: "Topped up {{kg}} kg",
        recovered: "Recovered {{kg}} kg",
      },
      showFewer: "Show fewer",
      seeAll: "See all {{total}}",
      callout: "Every top-up counts as a leak: its kg × GWP is added to Scope 1 carbon.",
    },
    units: {
      mwh: "{{value}} MWh",
      kwh: "{{value}} kWh",
      kg: "{{value}} kg",
      t: "{{value}} t",
      mwhAxis: "MWh",
    },
  },
} as const;
